import React, { useState, useEffect, useCallback, useRef } from "react";
import { AreaChart, Area, Line, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Target, TrendingUp, CheckCircle2, AlertCircle, Plus, Pencil, Trash2, X, GripVertical } from "lucide-react";
import { postRetirement, getProfile, addGoal, updateGoal, deleteGoal, reorderGoals } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Loader } from "../components/Primitives";
import { POS, NEG } from "../lib/format";

const RISK_LABELS = { conservative: "Conservative", balanced: "Balanced", aggressive: "Aggressive" };

export default function Retirement() {
  const { money } = useApp();
  const [p, setP] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [goalModal, setGoalModal] = useState(null);
  const dragId = useRef(null);

  const run = useCallback(async (params) => {
    setLoading(true);
    try { const r = await postRetirement(params); setRes(r); setGoals(r.goals || []); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    getProfile().then((d) => { setP(d.profile); run(d.profile); });
  }, [run]);

  if (!p) return <Loader label="Loading retirement profile…" />;

  const upd = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const sliders = [
    { k: "current_age", label: "Current Age", min: 18, max: 60, suf: "yrs" },
    { k: "retirement_age", label: "Target Retirement Age", min: 40, max: 70, suf: "yrs" },
    { k: "monthly_sip", label: "Monthly SIP", min: 6000, max: 500000, step: 1000, money: true },
    { k: "annual_step_up", label: "Annual Step-Up", min: 0, max: 25, suf: "%" },
    { k: "post_retirement_expense", label: "Post-Retirement Monthly Expense", min: 30000, max: 500000, step: 5000, money: true },
    { k: "inflation", label: "Inflation Assumption", min: 3, max: 10, step: 0.5, suf: "%" },
  ];

  const onDropGoal = async (targetId) => {
    const from = goals.findIndex((g) => g.id === dragId.current);
    const to = goals.findIndex((g) => g.id === targetId);
    dragId.current = null;
    if (from < 0 || to < 0 || from === to) return;
    const next = [...goals];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setGoals(next);
    try { const r = await reorderGoals(next.map((g) => g.id)); setGoals(r.goals); } catch { /* keep local order */ }
  };

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Controls */}
        <Card className="p-5" testid="retirement-controls">
          <div className="flex items-center gap-2 mb-4"><Target className="w-4 h-4 text-[#00e5ff]" /><Overline>Retirement Profile</Overline></div>
          <div className="space-y-4">
            {sliders.map((s) => (
              <div key={s.k}>
                <div className="flex justify-between mb-1.5">
                  <label className="overline">{s.label}</label>
                  <span className="font-mono text-xs text-[#f8fafc]">{s.money ? money(p[s.k], { compact: true }) : `${p[s.k]}${s.suf || ""}`}</span>
                </div>
                <input data-testid={`retirement-${s.k}-slider`} type="range" min={s.min} max={s.max} step={s.step || 1}
                  value={p[s.k]} onChange={(e) => upd(s.k, +e.target.value)} className="w-full" />
              </div>
            ))}
          </div>
          <button data-testid="run-forecast-btn" onClick={() => run(p)}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium py-2.5 hover:bg-[#33eaff] transition-colors">
            <TrendingUp className="w-4 h-4" /> Run Monte-Carlo Forecast
          </button>
        </Card>

        {/* Projection */}
        <Card className="lg:col-span-2 p-5" testid="projection-card">
          {loading || !res ? <Loader label="Running 400 simulations…" /> : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <Overline>Wealth Projection · Monte-Carlo Confidence Bands</Overline>
                <Badge color={res.on_track ? POS : "#ffb300"}>
                  {res.on_track ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                  {res.success_rate}% success
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div><div className="overline">Conservative (P10)</div><div className="font-mono text-lg text-[#ff8f3b]">{money(res.p10_final, { compact: true })}</div></div>
                <div><div className="overline">Median (P50)</div><div className="font-mono text-lg text-[#00e676]">{money(res.median_final, { compact: true })}</div></div>
                <div><div className="overline">Optimistic (P90)</div><div className="font-mono text-lg text-[#00e5ff]">{money(res.p90_final, { compact: true })}</div></div>
              </div>
              <div className="h-[260px] -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={res.projection}>
                    <defs>
                      <linearGradient id="band" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#00e5ff" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="age" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(a) => `${a}y`} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} width={60} tickFormatter={(v) => money(v, { compact: true })} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0f1420", border: "1px solid #2d3c59", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                      labelFormatter={(a) => `Age ${a}`} formatter={(v, n) => [money(v, { compact: true }), n]} />
                    <ReferenceLine y={res.required_corpus} stroke="#ffb300" strokeDasharray="4 4" label={{ value: "FIRE target", fill: "#ffb300", fontSize: 10, position: "insideTopLeft" }} />
                    <Area type="monotone" dataKey="p90" stroke="#00e5ff" strokeWidth={1} fill="url(#band)" name="P90 Optimistic" />
                    <Line type="monotone" dataKey="p50" stroke="#00e676" strokeWidth={2.5} dot={false} name="P50 Median" />
                    <Line type="monotone" dataKey="p10" stroke="#ff8f3b" strokeWidth={2} strokeDasharray="4 4" dot={false} name="P10 Conservative" />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono", color: "#94a3b8" }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between mt-2 font-mono text-xs text-[#64748b]">
                <span>Inflation-adjusted FIRE number: <span className="text-[#ffb300]">{money(res.required_corpus, { compact: true })}</span></span>
                <span>Starting corpus: <span className="text-[#f8fafc]">{money(res.current_corpus, { compact: true })}</span></span>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Goals */}
      <Card className="p-5" testid="goals-card">
        <div className="flex items-center justify-between mb-4">
          <Overline>Financial Goals · Target vs Actual · drag to set priority</Overline>
          <button data-testid="add-goal-btn" onClick={() => setGoalModal({ mode: "add", goal: {} })}
            className="flex items-center gap-1.5 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium px-3 py-1.5 text-xs hover:bg-[#33eaff]"><Plus className="w-3.5 h-3.5" /> Add Goal</button>
        </div>
        {goals.length === 0 ? <div className="text-sm text-[#64748b] text-center py-6">No goals yet. Add your first financial goal.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {goals.map((g, idx) => (
              <div key={g.id} draggable data-testid={`goal-${g.id}`}
                onDragStart={() => { dragId.current = g.id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropGoal(g.id)}
                className="rounded-lg border border-[#1e293b] p-4 group cursor-grab active:cursor-grabbing hover:border-[#2d3c59] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <GripVertical className="w-3.5 h-3.5 text-[#2d3c59] flex-shrink-0" />
                    <span data-testid={`goal-priority-${g.id}`} className="font-mono text-[10px] text-[#a855f7] flex-shrink-0">P{idx + 1}</span>
                    <span className="text-sm font-medium text-[#f8fafc] truncate">{g.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-xs text-[#64748b]">{g.year}</span>
                    <button data-testid={`edit-goal-${g.id}`} onClick={() => setGoalModal({ mode: "edit", goal: g })} className="text-[#64748b] hover:text-[#00e5ff]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-goal-${g.id}`} onClick={async () => { const r = await deleteGoal(g.id); setGoals(r.goals); }} className="text-[#64748b] hover:text-[#ff3b30]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="font-mono text-xs text-[#94a3b8] mt-1">{money(g.current, { compact: true })} <span className="text-[#64748b]">/ {money(g.target, { compact: true })}</span></div>
                <div className="flex items-center gap-2 mt-1">
                  {g.risk_profile && <Badge color="#a855f7">{RISK_LABELS[g.risk_profile] || g.risk_profile}</Badge>}
                  {g.linked_count > 0 && <span className="font-mono text-[10px] text-[#00e5ff]">◎ {g.linked_count} holding{g.linked_count > 1 ? "s" : ""} linked · {money(g.linked_value, { compact: true })}</span>}
                </div>
                <div className="h-2 rounded-full bg-[#1e293b] mt-3 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${g.progress}%`, background: g.progress >= 100 ? POS : g.progress >= 50 ? "#00e5ff" : "#ffb300" }} />
                </div>
                <div className="font-mono text-xs mt-1.5" style={{ color: g.progress >= 100 ? POS : "#94a3b8" }}>{g.progress}% funded</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AnimatePresence>
        {goalModal && <GoalModal mode={goalModal.mode} goal={goalModal.goal} onClose={() => setGoalModal(null)}
          onSaved={(gls) => { setGoals(gls); setGoalModal(null); }} />}
      </AnimatePresence>
    </div>
  );
}

function GoalModal({ mode, goal, onClose, onSaved }) {
  const [name, setName] = useState(goal.name || "");
  const [target, setTarget] = useState(goal.target || "");
  const [current, setCurrent] = useState(goal.current || "");
  const [year, setYear] = useState(goal.year || 2030);
  const [risk, setRisk] = useState(goal.risk_profile || "balanced");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name || !target) return;
    setBusy(true);
    const body = { name, target: Number(target), current: Number(current || 0), year: Number(year), risk_profile: risk };
    try {
      const r = mode === "add" ? await addGoal(body) : await updateGoal(goal.id, body);
      onSaved(r.goals);
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(4,7,13,0.7)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()} className="ao-card w-full max-w-md p-6" data-testid="goal-modal">
        <div className="flex items-center justify-between mb-4"><div className="text-lg font-semibold">{mode === "add" ? "Add Goal" : "Edit Goal"}</div><button onClick={onClose} className="text-[#64748b] hover:text-[#f8fafc]"><X className="w-5 h-5" /></button></div>
        <label className="overline">Goal Name</label>
        <input data-testid="goal-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 mb-3 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-3 py-2 text-sm text-[#f8fafc]" />
        <div className="grid grid-cols-3 gap-3">
          <div><label className="overline">Target ₹</label><input data-testid="goal-target" type="number" value={target} onChange={(e) => setTarget(e.target.value)} className="w-full mt-1 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-2 py-2 font-mono text-sm text-[#f8fafc]" /></div>
          <div><label className="overline">Current ₹</label><input data-testid="goal-current" type="number" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full mt-1 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-2 py-2 font-mono text-sm text-[#f8fafc]" /></div>
          <div><label className="overline">Year</label><input data-testid="goal-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-full mt-1 bg-[#0f1420] border border-[#2d3c59] rounded-lg px-2 py-2 font-mono text-sm text-[#f8fafc]" /></div>
        </div>
        <label className="overline block mt-4">Target Mix (Risk) · saved with goal</label>
        <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden mt-1">
          {Object.keys(RISK_LABELS).map((rk) => (
            <button key={rk} data-testid={`goal-modal-risk-${rk}`} onClick={() => setRisk(rk)}
              className={`flex-1 px-2.5 py-2 text-xs transition-colors ${risk === rk ? "bg-[#1a2338] text-[#a855f7]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>{RISK_LABELS[rk]}</button>
          ))}
        </div>
        <button data-testid="save-goal-btn" disabled={busy} onClick={save} className="mt-5 w-full rounded-lg bg-[#00e5ff] text-[#06121a] font-medium py-2.5 disabled:opacity-50">{busy ? "Saving…" : "Save Goal"}</button>
      </motion.div>
    </motion.div>
  );
}
